import { Box, Button, CircularProgress, Container, useTheme } from "@mui/material";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import SelectModeSubbarOpener from "../../Components/Banners/SelectModeBanner/opener";
import MovieCard, { MovieCardGrid, MovieCardProps } from "../../Components/Cards/MovieCard/movie-card";
import FiltersList from "../../Components/Filters/FiltersList/filters-list";
import Hero from "../../Components/Misc/Hero";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../Components/Misc/ListPageHeader";
import ToggleLayout from "../../Components/Misc/ToggleLayout";
import AutocompleteSearchCatalog from "../../Components/Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import TitleTable from "../../Components/Table/TitlesTable/titles-table";
import { assetService, cacheService, searchService, thumbnailService } from "../../Core/Api/services";
import { ThumbnailSize } from "../../Core/Api/Thumbnail/thumbnail-api-service";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import { MetadataFieldValueObjectTypes } from "../../Core/Api/Types/metadata-field-value-object-types";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { SearchRequestNative } from "../../Core/Api/Types/search-request-native";
import { SearchResponse } from "../../Core/Api/Types/search-response";
import { GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import { ComputeValueBaseTypeFromMetaBaseType } from "../../Core/Utils/functions";
import {
  AddOrUpdateFilter,
  GetConfidentialityFilter,
  GetFilters,
  GetHideWithoutAssetFilter,
  GetLimit,
  GetPattern,
  GetTitleCustomerIdsFilter,
  RemoveAllFilters,
  SetLimit,
  SetSearchPattern,
} from "../../Redux/Reducers/redux-slice-filters";
import { routePaths } from "../../Router/router";
import { RandomIntFromInterval } from "../../Utils/helpers";
import { handleSelectClickObject, isItemSelectedObject } from "../../Utils/Helpers/selectMode";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/system";

export type CatalogSearchHandlerProps = {
  setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
  selectedItems: DbAssetObjectInfos[];
};

const CatalogSearchHandler: React.FC<CatalogSearchHandlerProps> = (props) => {
  const { t } = useTranslation();
  const { selectedItems, setSelectedItems, ...otherProps } = props;

  const thumbSize: ThumbnailSize = ThumbnailSize.medium;

  const pattern = useSelector(GetPattern);
  const filters = useSelector(GetFilters);
  const limit = useSelector(GetLimit);
  const titleCustomerIdsFilter = useSelector(GetTitleCustomerIdsFilter);
  const hideWithoutAssetFilter = useSelector(GetHideWithoutAssetFilter);
  const confidentialityFilter = useSelector(GetConfidentialityFilter);
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [result, setResult] = React.useState<Omit<MovieCardProps, "index">[]>([]);
  const [resultObjects, setResultObjects] = React.useState<DbAssetObjectInfos[]>([]);
  const [cover, setCover] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);
  const [layout, setLayout] = React.useState<"grid" | "list">("grid");
  const [tableExtended, setTableExtended] = React.useState(false);

  const customerId = cacheService.GetCustomerExternalIdLabel();

  // Redux
  const dispatch = useDispatch();

  // Url params
  const mode = params.mode;
  const settings = params.settings;

  React.useEffect(() => {
    // Fetch the past search stored in Redux
    if (mode !== undefined || pattern !== "") return;

    console.log("Process results");

    // List of latest titles
    assetService.ListLastObjects(ObjectLayer.Title, 14).then(function (rep) {
      processResults(rep);
    });
  }, [mode, pattern]);

  React.useEffect(() => {
    if (mode === "find" && settings !== "" && settings !== undefined) {
      dispatch(SetSearchPattern(settings));
      dispatch(RemoveAllFilters());
      dispatch(SetLimit(null));

      console.debug("Setting pattern = [" + settings + "] from url for mode find.");
      navigate(routePaths.catalog.path);
      return;
    }

    if (mode === "series" || mode === "movies") {
      let metadataTitleTypeId = cacheService
        .GetMetadata()
        .find((m) => m.tagName === "titleType" && m.layer === ObjectLayer.Title)?.id;

      if (metadataTitleTypeId) {
        dispatch(SetSearchPattern("*"));
        dispatch(SetLimit(25));

        console.debug("Setting search series from url for mode find.");

        // Set titleType filter
        const metadataValue: IDbBaseMetadataValueInfos = {
          $type: ComputeValueBaseTypeFromMetaBaseType(BaseMetadataType.DbStringMetadataValueInfos),
          id: metadataTitleTypeId,
          fieldType: BaseMetadataType.DbStringMetadataValueInfos,
          value: mode === "series" ? "Season" : "Movie",
          creationTime: new Date().toISOString(),
          updateTime: null,
          fieldName: null,
          fieldLabel: null,
          targetLayer: ObjectLayer.Title,
          valueLabel: null,
        };

        dispatch(AddOrUpdateFilter(metadataValue));

        console.debug("Setting series from url for mode find.");
        navigate(routePaths.catalog.path);
        return;
      }
    }
  }, [mode, settings]);

  React.useEffect(() => {
    if (mode || settings) return;

    // Fetch the past search stored in Redux
    if (pattern && pattern !== "") {
      setIsLoading(true);
      setTableExtended(false);

      searchService
        .SearchBasic(
          new SearchRequestNative({
            logQuery: true,
            outputLayer: ObjectLayer.Title,
            hideTitleWithoutAsset: hideWithoutAssetFilter,
            onlyConfidential: confidentialityFilter,
            pattern: pattern,
            metadata: filters,
            titleCustomerIds: titleCustomerIdsFilter,
            limit: limit,
          })
        )
        .then(function (rep) {
          processResults(rep);
          setIsLoading(false);
        });

      console.log(
        "search did from UE with deps changes, mode = [" +
          mode +
          "], pattern = [" +
          pattern +
          "], settings = [" +
          settings +
          "]"
      );
    }
  }, [filters, settings, mode, pattern, limit, hideWithoutAssetFilter, titleCustomerIdsFilter, confidentialityFilter]);

  function setDataFromResearchField(data: SearchResponse) {
    processResults(data);
  }

  // Process result handler
  const processResults = (rep: SearchResponse) => {
    if (rep.results !== null) {
      if (rep.results.length > 0)
        setCover(thumbnailService.GetObjectPosterUrl(rep.results[RandomIntFromInterval(0, rep.results.length - 1)].id));

      let thisYear: number = new Date().getFullYear();

      let movieCardsData: Omit<MovieCardProps, "index">[] = rep.results.map((movie) => {
        let date = GetMetadataByTagName(movie, "productionDate")?.value;
        let movieYear = new Date(date).getFullYear();

        return {
          id: movie.id,
          obj: movie,
          to: "#",
          cover: thumbnailService.GetObjectPosterUrl(movie.id, thumbSize),
          title: movie.name ?? "",
          labelList: movie.isDeleted ? ["DELETED"] : movieYear === thisYear ? ["NEW"] : [movieYear.toString()],
          transparentChipText:
            GetMetadataByTagName(movie, "titleType")?.value +
            (customerId !== "" ? ` (${customerId} #${movie.titleCustomerId})` : ""),
          favorite: false,
          titleCustomerId: movie.titleCustomerId,
          expand: {
            contentList: [],
            audioList: [],
            subtitleList: [],
            tagList: [],
            quality: "",
          },
          selectMode: true,
        };
      });

      setResult(movieCardsData);
      setResultObjects(rep.results);
      if (movieCardsData.length < 300) setTableExtended(true);
      setIsLoading(false);
    }
  };

  const onLayoutChange = (e: React.MouseEvent<HTMLElement>, value: "grid" | "list") => {
    setLayout(value);
  };

  return (
    <Container maxWidth="xl">
      <SelectModeSubbarOpener selectedObjects={selectedItems} />
      <Hero src={cover} />

      <Box pt={37}>
        <ListPageHeader
          mb={2}
          sx={{
            borderRadius: "12px",
            marginLeft: theme.spacing(-2),
            marginRight: theme.spacing(-2),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            overflow: "visible",
          }}
        >
          <ListPageHeaderLeft
            title={
              isLoading
                ? "Searching..."
                : t("component:listPageHeaderLeft.title.resultsForPattern", {
                    count: result.length,
                    context: pattern !== "" ? "pattern" : "",
                    pattern,
                  })
            }
          >
            <FiltersList
              sx={{ mt: 2 }}
              layer={ObjectLayer.Title}
              type={MetadataFieldValueObjectTypes.DbAssetObjectInfos}
              disabled={!pattern || pattern === ""}
            />
          </ListPageHeaderLeft>
          <ListPageHeaderRight>
            <AutocompleteSearchCatalog
              onSearchBasicCallback={setDataFromResearchField}
              layer={ObjectLayer.Title}
              enableSearchCallback={false}
            />
            <ToggleLayout value={layout} onChange={onLayoutChange} />
            {layout === "list" && result.length > 300 && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setTableExtended(true)}
                disabled={tableExtended || isLoading}
              >
                {t("button:getDetailedTitlesInfos")}
              </Button>
            )}
          </ListPageHeaderRight>
        </ListPageHeader>
        {layout === "grid" ? (
          <>
            {isLoading ? (
              <CircularProgress
                sx={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                }}
              />
            ) : (
              <MovieCardGrid>
                {result?.map((movie, index) => (
                  <MovieCard
                    key={movie.id}
                    selected={isItemSelectedObject(resultObjects.find((x) => x.id === movie.id)!, selectedItems)}
                    selectedOnClick={selectedItems && selectedItems.length > 0 ? true : false}
                    onSelectClick={handleSelectClickObject(selectedItems, setSelectedItems)}
                    shareBtn={true}
                    orderBtn={true}
                    collectionBtn={true}
                    {...movie}
                  />
                ))}
              </MovieCardGrid>
            )}
          </>
        ) : (
          <Stack sx={{ height: "calc(100vh - 150px)" }}>
            <TitleTable
              loading={isLoading}
              titles={resultObjects}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              extended={tableExtended}
            />
          </Stack>
        )}
      </Box>
    </Container>
  );
};

export default CatalogSearchHandler;
